import { Alert, AlertDescription } from "@altimateai/lego";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { Link } from "react-router-dom";

const TABLEAU_CLOUD_SETUP_DOC =
  "https://docs.altimate.ai/docs/tableau-insights-setup-tableau-cloud#steps-to-upload-the-workbooks-manually";
const TABLEAU_SERVER_SETUP_DOC =
  "https://docs.altimate.ai/docs/tableau-insights-setup-tableau-server#steps-to-upload-the-workbooks-manually";

interface ProjectAlertsProps {
  useCustomLocation: boolean;
  defaultProjectName: string;
  subprojectName: string;
  isError: boolean;
  error: unknown;
  mustUseCustomLocation: boolean;
  /** True when the connecting PAT role can publish workbooks. When false
   * (Site Administrator Explorer), the copy switches from "create" to
   * "locate" — we cannot publish so the workbooks must already exist. */
  canPublishWorkbooks?: boolean;
  /** Tableau Cloud vs Tableau Server — drives which setup doc URL to link. */
  isTableauCloud?: boolean;
}

export const ProjectAlerts = ({
  useCustomLocation,
  defaultProjectName,
  subprojectName,
  isError,
  error,
  mustUseCustomLocation,
  canPublishWorkbooks = true,
  isTableauCloud = true,
}: ProjectAlertsProps) => {
  const setupDocUrl = isTableauCloud ? TABLEAU_CLOUD_SETUP_DOC : TABLEAU_SERVER_SETUP_DOC;
  return (
    <>
      <Alert className="al-mb-4" variant="info">
        <IconInfoCircle className="al-h-4 al-w-4 al-text-blue-600" />
        <AlertDescription className="al-text-sm al-text-gray-700">
          {canPublishWorkbooks ? (
            !useCustomLocation ? (
              <>
                Altimate will create a{" "}
                <span className="al-font-semibold">{defaultProjectName}</span> subproject inside
                Tableau&#39;s Default project to organize generated workbooks.
              </>
            ) : (
              <>
                Altimate will create a new subproject called{" "}
                <span className="al-font-semibold">{subprojectName}</span> inside the selected
                project to organize generated workbooks.
              </>
            )
          ) : (
            <>
              Your PAT cannot publish workbooks. Tell us where you uploaded the{" "}
              <span className="al-font-semibold">{subprojectName}</span> subproject —
              {!useCustomLocation
                ? " by default we look under Tableau's Default project."
                : " pick the parent project below."}
            </>
          )}
        </AlertDescription>
      </Alert>

      {isError && (
        <Alert className="al-mb-4 al-bg-red-50 al-border-red-200">
          <IconAlertCircle className="al-h-4 al-w-4 al-text-red-600" />
          <AlertDescription className="al-text-sm al-text-gray-700">
            <div className="al-font-semibold">Failed to load Tableau projects</div>
            <div className="al-mt-1">
              {(error as Error)?.message ||
                "Please check your connection credentials and try again."}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {mustUseCustomLocation && (
        <Alert className="al-mb-4 al-bg-orange-50 al-border-orange-200">
          <IconAlertCircle className="al-h-4 al-w-4 al-text-orange-600" />
          <AlertDescription className="al-text-sm al-text-gray-700">
            {canPublishWorkbooks ? (
              <>
                The <span className="al-font-semibold">{defaultProjectName}</span> subproject
                already exists under the Default project and contains workbooks. Please select a
                different parent project.
              </>
            ) : (
              <>
                We couldn&#39;t auto-detect the Altimate workbooks under Tableau&#39;s Default
                project. Pick the parent project where you uploaded them.{" "}
                <span>
                  If the workbooks were not already uploaded, refer to{" "}
                  <Link
                    to={setupDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="al-text-blue-600 al-underline hover:al-text-blue-700"
                  >
                    this link
                  </Link>{" "}
                  to download and upload them first.
                </span>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
